<template>
  <div class="max-h-[65vh] overflow-y-auto space-y-6 pr-1">
    <!-- Presenting Complaint -->
    <section class="border rounded-lg p-4">
      <h3 class="font-semibold text-custom-blue mb-3">
        Presenting Complaint{{ scopeSuffix }}
      </h3>
      <div v-if="!hasComplaintContent" class="text-sm text-gray-400">Not recorded yet.</div>
      <div v-else class="grid grid-cols-2 gap-4 text-sm">
        <div class="col-span-2">
          <p class="font-semibold text-gray-600">Complaints:</p>
          <div v-if="viewSymptoms.length" class="space-y-1 mt-1">
            <div v-for="(s, i) in viewSymptoms" :key="i" class="flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-custom-blue inline-block shrink-0"></span>
              <span>{{ s.chief_complaint }}</span>
              <span v-if="s.duration" class="text-gray-500">— {{ s.duration }} {{ s.duration_type }}</span>
            </div>
          </div>
          <p v-else class="text-gray-400">—</p>
        </div>
        <div v-if="existingComplaint.history_of_illness">
          <p class="font-semibold text-gray-600">History of Illness:</p>
          <p>{{ existingComplaint.history_of_illness }}</p>
        </div>
        <div v-if="existingComplaint.menstrual_period">
          <p class="font-semibold text-gray-600">Last Menstrual Period:</p>
          <p>{{ existingComplaint.menstrual_period }}</p>
        </div>
        <div v-if="existingSocialHistories.length">
          <p class="font-semibold text-gray-600">Social History:</p>
          <p>{{ existingSocialHistories.join(", ") }}</p>
        </div>
        <div v-if="existingFamilyHistories.length">
          <p class="font-semibold text-gray-600">Family History:</p>
          <p>{{ existingFamilyHistories.join(", ") }}</p>
        </div>
        <div v-if="existingPreliminaryDiagnosis.length" class="col-span-2">
          <p class="font-semibold text-gray-600">Preliminary Diagnosis:</p>
          <p>{{ existingPreliminaryDiagnosis.map(d => d.diagnosis?.name).filter(Boolean).join(", ") }}</p>
        </div>
      </div>
    </section>

    <!-- Physical Exam -->
    <section class="border rounded-lg p-4">
      <h3 class="font-semibold text-custom-blue mb-3">
        Physical Exam{{ scopeSuffix }}
      </h3>
      <div v-if="!hasPhysicalExamContent" class="text-sm text-gray-400">Not recorded yet.</div>
      <div v-else class="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        <div v-for="field in physicalExamFields" :key="field.key" class="flex items-start gap-2">
          <span class="w-40 text-gray-500 shrink-0">{{ field.label }}:</span>
          <span class="text-gray-800">{{ existingPhysicalExam[field.key] || "—" }}</span>
        </div>
      </div>
    </section>

    <!-- Diagnosis -->
    <section class="border rounded-lg p-4">
      <h3 class="font-semibold text-custom-blue mb-3">Diagnosis</h3>
      <div v-if="!hasDiagnosisContent" class="text-sm text-gray-400">Not recorded yet.</div>
      <div v-else class="text-sm space-y-2">
        <div v-if="existingFindings">
          <p class="font-semibold text-gray-600">Findings:</p>
          <p>{{ existingFindings }}</p>
        </div>
        <div v-if="existingFinalDiagnosis.length">
          <p class="font-semibold text-gray-600">Final Diagnosis:</p>
          <p>{{ existingFinalDiagnosis.map(d => d.diagnosis?.name).filter(Boolean).join(", ") }}</p>
        </div>
      </div>
    </section>

    <!-- Prescriptions -->
    <section class="border rounded-lg p-4">
      <h3 class="font-semibold text-custom-blue mb-3">Prescriptions</h3>
      <div v-if="!allPrescriptionRows.length" class="text-sm text-gray-400">No prescriptions recorded.</div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 text-sm">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-2 text-left font-medium text-gray-500">Source</th>
              <th class="px-4 py-2 text-left font-medium text-gray-500">Drug</th>
              <th class="px-4 py-2 text-left font-medium text-gray-500">Dosage</th>
              <th class="px-4 py-2 text-left font-medium text-gray-500">Frequency</th>
              <th class="px-4 py-2 text-left font-medium text-gray-500">Duration</th>
              <th class="px-4 py-2 text-left font-medium text-gray-500">Route</th>
              <th class="px-4 py-2 text-left font-medium text-gray-500">Notes</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="(p, index) in allPrescriptionRows" :key="index">
              <td class="px-4 py-1.5">
                <span
                  class="text-xs px-2 py-0.5 rounded-full font-medium"
                  :class="p.source === 'External' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'"
                >
                  {{ p.source }}
                </span>
              </td>
              <td class="px-4 py-1.5">{{ p.drug || "—" }}</td>
              <td class="px-4 py-1.5">{{ p.dosage || "—" }}</td>
              <td class="px-4 py-1.5">{{ p.frequency || "—" }}</td>
              <td class="px-4 py-1.5">{{ p.duration || "—" }}</td>
              <td class="px-4 py-1.5">{{ p.route || "—" }}</td>
              <td class="px-4 py-1.5">{{ p.notes || "—" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Procedures -->
    <section class="border rounded-lg p-4">
      <h3 class="font-semibold text-custom-blue mb-3">Procedures</h3>
      <div v-if="proceduresWithResults.length === 0" class="text-sm text-gray-400">
        No procedures recorded for this visit.
      </div>
      <div v-else class="grid grid-cols-1 gap-4">
        <div
          v-for="proc in proceduresWithResults"
          :key="proc.id"
          class="border rounded-lg p-4"
        >
          <div class="flex items-center justify-between mb-3 pb-2 border-b">
            <h4 class="font-semibold text-sm">{{ procedureName(proc) }}</h4>
            <span
              class="text-xs px-2 py-0.5 rounded-full font-medium"
              :class="proc.status?.code === 'ST-005' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'"
            >
              {{ proc.status?.name || "Pending" }}
            </span>
          </div>

          <div class="grid grid-cols-2 gap-6 text-sm">
            <div>
              <div class="mb-3">
                <p class="font-semibold text-gray-600">Chief Complaint:</p>
                <p>{{ proc.procedure_result?.chief_complaint || "N/A" }}</p>
              </div>
              <div class="mb-3">
                <p class="font-semibold text-gray-600">Duration:</p>
                <p>
                  {{ proc.procedure_result?.duration || "N/A" }}
                  {{ proc.procedure_result?.duration_type || "" }}
                </p>
              </div>
              <div class="mb-3">
                <p class="font-semibold text-gray-600">History of Illness:</p>
                <p>{{ proc.procedure_result?.history_of_illness || "N/A" }}</p>
              </div>
              <div>
                <p class="font-semibold text-gray-600">Findings:</p>
                <p>{{ proc.procedure_result?.findings || "N/A" }}</p>
              </div>
            </div>

            <div>
              <p class="font-semibold text-gray-600 mb-2">Physical Exam:</p>
              <div class="space-y-1.5">
                <div
                  v-for="field in physicalExamFields"
                  :key="field.key"
                  class="flex items-start gap-2"
                >
                  <span class="w-40 text-gray-500 shrink-0">{{ field.label }}:</span>
                  <span class="text-gray-800">{{ proc.procedure_result?.[field.key] || "N/A" }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "EncounterSummaryTab",
  props: {
    mode: { type: String, default: "consultation" }, // 'consultation' | 'procedure'
    existingComplaint: { type: Object, default: () => ({}) },
    existingPhysicalExam: { type: Object, default: () => ({}) },
    existingPreliminaryDiagnosis: { type: Array, default: () => [] },
    existingFinalDiagnosis: { type: Array, default: () => [] },
    existingFindings: { type: String, default: "" },
    existingSocialHistories: { type: Array, default: () => [] },
    existingFamilyHistories: { type: Array, default: () => [] },
    existingPrescriptions: { type: Array, default: () => [] },
    existingExternalPrescription: { type: Object, default: () => null },
    existingProcedures: { type: Array, default: () => [] },
  },
  data() {
    return {
      physicalExamFields: [
        { key: "general", label: "General" },
        { key: "respiratory_system", label: "Respiratory System" },
        { key: "cadiovascular_system", label: "Cardiovascular System" },
        { key: "abdomen", label: "Abdomen" },
        { key: "genitourinary", label: "Genitourinary" },
        { key: "ent", label: "ENT" },
        { key: "skin", label: "Skin" },
        { key: "musculoskeletal", label: "Musculoskeletal" },
        { key: "cns", label: "CNS" },
      ],
    };
  },
  computed: {
    scopeSuffix() {
      return this.mode === "procedure" ? " (current procedure)" : "";
    },
    viewSymptoms() {
      const existing = this.existingComplaint;
      if (existing?.symptoms?.length) return existing.symptoms;
      if (existing?.chief_complaint) {
        return [{ chief_complaint: existing.chief_complaint, duration: existing.duration, duration_type: existing.duration_type }];
      }
      return [];
    },
    hasComplaintContent() {
      return (
        this.viewSymptoms.length > 0 ||
        !!this.existingComplaint?.history_of_illness ||
        !!this.existingComplaint?.menstrual_period ||
        this.existingSocialHistories.length > 0 ||
        this.existingFamilyHistories.length > 0 ||
        this.existingPreliminaryDiagnosis.length > 0
      );
    },
    hasPhysicalExamContent() {
      return this.physicalExamFields.some((f) => !!this.existingPhysicalExam?.[f.key]);
    },
    hasDiagnosisContent() {
      return !!this.existingFindings || this.existingFinalDiagnosis.length > 0;
    },
    activePrescriptions() {
      return (this.existingPrescriptions || []).filter((p) => p.status?.code !== "ST-019");
    },
    externalPrescriptionRows() {
      return (this.existingExternalPrescription?.items || []).map((item) => ({
        source: "External",
        drug: item.drug_name,
        dosage: item.dose ? `${item.dose} ${item.dose_unit || ""}`.trim() : "",
        frequency: item.inventory_dosage_frequency?.name,
        duration: item.days ? `${item.days} days` : "",
        route: item.administration_route?.name,
        notes: item.notes,
      }));
    },
    allPrescriptionRows() {
      const internal = this.activePrescriptions.map((p) => ({
        source: "Internal",
        drug: p.inventory_item?.trade_name,
        dosage: p.dose,
        frequency: p.inventory_dosage_frequency?.name,
        duration: p.days ? `${p.days} days` : "",
        route: p.administration_route?.name,
        notes: p.notes,
      }));
      return [...internal, ...this.externalPrescriptionRows];
    },
    proceduresWithResults() {
      return (this.existingProcedures || []).filter(
        (p) => p.id && this.hasProcedureContent(p.procedure_result)
      );
    },
  },
  methods: {
    hasProcedureContent(result) {
      if (!result) return false;
      const fields = [
        "chief_complaint", "duration", "history_of_illness", "findings",
        "general", "respiratory_system", "cadiovascular_system", "abdomen",
        "genitourinary", "ent", "skin", "musculoskeletal", "cns",
      ];
      return fields.some((f) => (result[f] || "").toString().trim().length > 0);
    },
    procedureName(proc) {
      return proc?.facility_service?.name
          || proc?.facility_service_name
          || proc?.description
          || "Procedure";
    },
  },
};
</script>

<style lang="scss" scoped></style>
